// Utilidad para detectar autom├íticamente las posiciones de las etiquetas en el PDF
// Usa pdf-lib para parsear el content stream del PDF y extraer posiciones de texto.
// Si falla por cualquier motivo, devuelve null y el caller usa posiciones fijas.

import { PDFDocument, PDFName, PDFArray, PDFRawStream, decodePDFRawStream } from 'pdf-lib';

// Textos que buscamos en el PDF (normalizados)
const TARGETS = [
  { key: "km",           patterns: ["km totales"] },
  { key: "horasDespl",   patterns: ["horas desplazamiento", "horas desplaz"] },
  { key: "horasObra",    patterns: ["horas obra"] },
  { key: "operarios",    patterns: ["operarios:","operarios"] },
  { key: "operaciones",  patterns: ["operaciones:", "operaciones"] },
  { key: "nombre",       patterns: ["nombre, apellidos", "nombre"] },
  { key: "dni",          patterns: ["dni"] },
  { key: "firmado",      patterns: ["firmado"] },
  { key: "datosTecnico", patterns: ["datos t", "datos tecnico"] },
];

function norm(s) {
  return String(s ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}



/**
 * Extrae todos los rect├íngulos del content stream del PDF.
 * Detecta tanto operadores "re" como rect├íngulos formados por l├¡neas (m/l/h).
 * Trackea la CTM (Current Transformation Matrix) para manejar q/Q y cm.
 */
function extractRectangles(contentBytes) {
  let raw;
  if (contentBytes instanceof Uint8Array) {
    raw = new TextDecoder("latin1").decode(contentBytes);
  } else {
    raw = String(contentBytes);
  }

  const rects = [];
  const tokens = tokenize(raw);
  const stack = [];
  // CTM stack for q/Q
  let ctmStack = [];
  let ctm = { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 };
  
  // Track path points for line-based rectangles
  let pathPoints = [];
  let pathStartX = 0, pathStartY = 0;

  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];

    if (t === "q") {
      ctmStack.push({ ...ctm });
      stack.length = 0;
      continue;
    }
    if (t === "Q") {
      if (ctmStack.length > 0) ctm = ctmStack.pop();
      stack.length = 0;
      continue;
    }
    if (t === "cm") {
      if (stack.length >= 6) {
        const vals = stack.slice(stack.length - 6).map(Number);
        if (vals.every(v => !isNaN(v))) {
          const [a, b, c, d, e, f] = vals;
          const na = a * ctm.a + b * ctm.c;
          const nb = a * ctm.b + b * ctm.d;
          const nc = c * ctm.a + d * ctm.c;
          const nd = c * ctm.b + d * ctm.d;
          const ne = e * ctm.a + f * ctm.c + ctm.e;
          const nf = e * ctm.b + f * ctm.d + ctm.f;
          ctm = { a: na, b: nb, c: nc, d: nd, e: ne, f: nf };
        }
      }
      stack.length = 0;
      continue;
    }
    
    // Move to point (start of path)
    if (t === "m") {
      if (stack.length >= 2) {
        const mx = parseFloat(stack[stack.length - 2]);
        const my = parseFloat(stack[stack.length - 1]);
        if (!isNaN(mx) && !isNaN(my)) {
          const ax = mx * ctm.a + my * ctm.c + ctm.e;
          const ay = mx * ctm.b + my * ctm.d + ctm.f;
          pathPoints = [{ x: ax, y: ay }];
          pathStartX = ax;
          pathStartY = ay;
        }
      }
      stack.length = 0;
      continue;
    }
    
    // Line to point
    if (t === "l") {
      if (stack.length >= 2) {
        const lx = parseFloat(stack[stack.length - 2]);
        const ly = parseFloat(stack[stack.length - 1]);
        if (!isNaN(lx) && !isNaN(ly)) {
          const ax = lx * ctm.a + ly * ctm.c + ctm.e;
          const ay = lx * ctm.b + ly * ctm.d + ctm.f;
          pathPoints.push({ x: ax, y: ay });
        }
      }
      stack.length = 0;
      continue;
    }
    
    // Close path
    if (t === "h") {
      if (pathPoints.length >= 3) {
        pathPoints.push({ x: pathStartX, y: pathStartY });
      }
      stack.length = 0;
      continue;
    }
    
    if (t === "re") {
      if (stack.length >= 4) {
        const vals = stack.slice(stack.length - 4).map(Number);
        if (vals.every(v => !isNaN(v))) {
          let [rx, ry, rw, rh] = vals;
          const ax = rx * ctm.a + ry * ctm.c + ctm.e;
          const ay = rx * ctm.b + ry * ctm.d + ctm.f;
          const aw = Math.abs(rw * ctm.a);
          const ah = Math.abs(rh * ctm.d);
          rects.push({
            x: ax,
            y: rh < 0 ? ay + rh * ctm.d : ay,
            w: aw,
            h: ah,
            source: "re",
          });
        }
      }
      stack.length = 0;
      continue;
    }
    
    // Stroke/Fill/Close operators ÔÇö check if path forms a rectangle
    if (t === "S" || t === "s" || t === "f" || t === "F" || t === "f*" ||
        t === "B" || t === "B*" || t === "b" || t === "b*") {
      // Check if pathPoints forms a rectangle (4 or 5 points forming right angles)
      if (pathPoints.length >= 4 && pathPoints.length <= 6) {
        const pts = pathPoints;
        const xs = pts.map(p => p.x);
        const ys = pts.map(p => p.y);
        const minX = Math.min(...xs), maxX = Math.max(...xs);
        const minY = Math.min(...ys), maxY = Math.max(...ys);
        const w = maxX - minX;
        const h = maxY - minY;
        
        // Check if all points are on the edges of the bounding box (rectangular path)
        if (w > 20 && h > 10) {
          const allOnEdge = pts.every(p => {
            const onLeft = Math.abs(p.x - minX) < 2;
            const onRight = Math.abs(p.x - maxX) < 2;
            const onBottom = Math.abs(p.y - minY) < 2;
            const onTop = Math.abs(p.y - maxY) < 2;
            return onLeft || onRight || onBottom || onTop;
          });
          
          if (allOnEdge) {
            rects.push({ x: minX, y: minY, w, h, source: "path" });
          }
        }
      }
      pathPoints = [];
      stack.length = 0;
      continue;
    }
    
    // New path operator resets path
    if (t === "n" || t === "W" || t === "W*") {
      pathPoints = [];
      stack.length = 0;
      continue;
    }

    // For other operators, just clear the stack
    if (t === "BT" || t === "ET" || t === "Tj" || t === "TJ" ||
        t === "Td" || t === "TD" || t === "Tm" || t === "Tf" || t === "T*" ||
        t === "g" || t === "G" || t === "rg" || t === "RG" || t === "k" || t === "K" ||
        t === "w" || t === "J" || t === "j" || t === "M" || t === "d" ||
        t === "Do" || t === "gs" || t === "cs" || t === "CS" || t === "sc" || t === "SC" ||
        t === "scn" || t === "SCN" || t === "ri" || t === "i" || t === "c" || t === "v" || t === "y") {
      stack.length = 0;
      continue;
    }

    // Accumulate operands
    stack.push(t);
  }

  return rects;
}

/**
 * Encuentra el rect├íngulo que contiene o est├í m├ís cerca de la etiqueta "Firmado".
 * La etiqueta suele estar en la cabecera gris del recuadro (parte superior).
 * Busca recuadros que contengan la etiqueta o est├®n muy cerca.
 */
function findBoxNearLabel(rects, labelX, labelY, minW = 80, minH = 40) {
  // Filtrar rect├íngulos que sean lo suficientemente grandes para ser un recuadro de firma
  const candidates = rects.filter(r => r.w >= minW && r.h >= minH && r.w < 400 && r.h < 300);

  if (candidates.length === 0) return null;

  let best = null;
  let bestDist = Infinity;

  // PASO 1: Buscar recuadro que CONTENGA la etiqueta (la etiqueta est├í dentro del box)
  for (const r of candidates) {
    const rTop = r.y + r.h;
    const insideX = labelX >= r.x - 10 && labelX <= r.x + r.w + 10;
    const insideY = labelY >= r.y - 5 && labelY <= rTop + 5;
    if (insideX && insideY) {
      const dist = Math.abs(labelX - r.x) + Math.abs(rTop - labelY);
      if (dist < bestDist) {
        bestDist = dist;
        best = r;
      }
    }
  }
  if (best) return best;

  // PASO 2: Buscar recuadro cuyo borde superior est├® cerca de la etiqueta
  bestDist = Infinity;
  for (const r of candidates) {
    const rTop = r.y + r.h;
    const insideX = labelX >= r.x - 15 && labelX <= r.x + r.w + 15;
    const nearTop = Math.abs(rTop - labelY) < 25;
    if (insideX && nearTop) {
      const dist = Math.abs(rTop - labelY);
      if (dist < bestDist) {
        bestDist = dist;
        best = r;
      }
    }
  }
  if (best) return best;

  // PASO 3: Buscar el m├ís cercano en general (dentro de 60pt)
  bestDist = Infinity;
  for (const r of candidates) {
    const rTop = r.y + r.h;
    const rCenterX = r.x + r.w / 2;
    const dist = Math.sqrt(Math.pow(rCenterX - labelX, 2) + Math.pow(rTop - labelY, 2));
    if (dist < 60 && dist < bestDist) {
      bestDist = dist;
      best = r;
    }
  }

  return best;
}

/**
 * Encuentra el rect├íngulo que contiene la etiqueta "Datos t├®cnico" / "Nombre" / "DNI".
 * Busca un recuadro que contenga esas etiquetas, con tolerancia generosa.
 */
function findBoxContainingLabel(rects, labelX, labelY, minW = 100, minH = 30) {
  const candidates = rects.filter(r => r.w >= minW && r.h >= minH && r.w < 400 && r.h < 300);

  // Primero buscar contenimiento estricto
  for (const r of candidates) {
    const rTop = r.y + r.h;
    if (labelX >= r.x - 5 && labelX <= r.x + r.w + 5 &&
        labelY >= r.y - 5 && labelY <= rTop + 5) {
      return r;
    }
  }
  
  // Luego buscar con tolerancia m├ís amplia (15pt)
  for (const r of candidates) {
    const rTop = r.y + r.h;
    if (labelX >= r.x - 15 && labelX <= r.x + r.w + 15 &&
        labelY >= r.y - 15 && labelY <= rTop + 15) {
      return r;
    }
  }
  
  return null;
}

/**
 * Parsea el content stream de una p├ígina PDF y extrae fragmentos de texto con sus posiciones.
 * Interpreta los operadores PDF de posicionamiento de texto (Tm, Td, TD, Tf, Tj, TJ, ', ").
 */
function extractTextItems(contentBytes) {
  // Convertir bytes a string
  let raw;
  if (contentBytes instanceof Uint8Array) {
    raw = new TextDecoder("latin1").decode(contentBytes);
  } else {
    raw = String(contentBytes);
  }

  const items = [];

  // Estado de la matriz de texto
  let fontSize = 12;
  let tx = 0, ty = 0;       // posici├│n actual de texto
  let tmA = 1, tmB = 0, tmC = 0, tmD = 1, tmE = 0, tmF = 0; // text matrix
  let inText = false;

  // Tokenizar el content stream de forma simple
  const tokens = tokenize(raw);

  const stack = [];

  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];

    if (t === "BT") {
      inText = true;
      tmA = 1; tmB = 0; tmC = 0; tmD = 1; tmE = 0; tmF = 0;
      tx = 0; ty = 0;
      continue;
    }
    if (t === "ET") {
      inText = false;
      stack.length = 0;
      continue;
    }

    if (!inText) {
      // Fuera de bloque de texto, ignorar
      stack.length = 0;
      continue;
    }

    // Operadores de texto
    switch (t) {
      case "Tf": {
        // stack: [fontName, fontSize]
        if (stack.length >= 2) {
          const sz = parseFloat(stack[stack.length - 2]);
          if (!isNaN(sz) && sz > 0) fontSize = sz;
        }
        stack.length = 0;
        break;
      }
      case "Tm": {
        // stack: [a, b, c, d, e, f]
        if (stack.length >= 6) {
          const vals = stack.slice(stack.length - 6).map(Number);
          if (vals.every(v => !isNaN(v))) {
            [tmA, tmB, tmC, tmD, tmE, tmF] = vals;
            tx = tmE;
            ty = tmF;
          }
        }
        stack.length = 0;
        break;
      }
      case "Td": {
        if (stack.length >= 2) {
          const tdx = parseFloat(stack[stack.length - 2]);
          const tdy = parseFloat(stack[stack.length - 1]);
          if (!isNaN(tdx) && !isNaN(tdy)) {
            tmE += tdx * tmA + tdy * tmC;
            tmF += tdx * tmB + tdy * tmD;
            tx = tmE;
            ty = tmF;
          }
        }
        stack.length = 0;
        break;
      }
      case "TD": {
        // Same as Td but also sets leading
        if (stack.length >= 2) {
          const tdx = parseFloat(stack[stack.length - 2]);
          const tdy = parseFloat(stack[stack.length - 1]);
          if (!isNaN(tdx) && !isNaN(tdy)) {
            tmE += tdx * tmA + tdy * tmC;
            tmF += tdx * tmB + tdy * tmD;
            tx = tmE;
            ty = tmF;
          }
        }
        stack.length = 0;
        break;
      }
      case "T*": {
        // Move to start of next line (uses leading)
        // Approximate: move down by fontSize
        tmF -= fontSize * tmD;
        ty = tmF;
        stack.length = 0;
        break;
      }
      case "Tj": {
        // Show text string
        if (stack.length >= 1) {
          const text = extractStringFromToken(stack[stack.length - 1]);
          if (text.trim()) {
            items.push({
              str: text,
              x: tx,
              y: ty,
              fontSize: Math.abs(fontSize * tmD) || fontSize,
            });
          }
          // Advance tx approximately
          tx += estimateTextWidth(text, fontSize) * tmA;
        }
        stack.length = 0;
        break;
      }
      case "TJ": {
        // Show text with individual glyph positioning (array)
        const arrStr = reconstructTJArray(stack);
        if (arrStr.trim()) {
          items.push({
            str: arrStr,
            x: tx,
            y: ty,
            fontSize: Math.abs(fontSize * tmD) || fontSize,
          });
          tx += estimateTextWidth(arrStr, fontSize) * tmA;
        }
        stack.length = 0;
        break;
      }
      case "'": {
        // Move to next line and show text
        tmF -= fontSize * tmD;
        ty = tmF;
        if (stack.length >= 1) {
          const text = extractStringFromToken(stack[stack.length - 1]);
          if (text.trim()) {
            items.push({ str: text, x: tx, y: ty, fontSize: Math.abs(fontSize * tmD) || fontSize });
          }
        }
        stack.length = 0;
        break;
      }
      case '"': {
        // Set word/char spacing, move to next line, show text
        tmF -= fontSize * tmD;
        ty = tmF;
        if (stack.length >= 1) {
          const text = extractStringFromToken(stack[stack.length - 1]);
          if (text.trim()) {
            items.push({ str: text, x: tx, y: ty, fontSize: Math.abs(fontSize * tmD) || fontSize });
          }
        }
        stack.length = 0;
        break;
      }
      default: {
        // Acumular operandos
        stack.push(t);
        break;
      }
    }
  }

  return items;
}

/**
 * Tokenizador simple para PDF content streams.
 * Separa operadores, n├║meros, strings entre par├®ntesis, hex strings, y nombres.
 */
function tokenize(raw) {
  const tokens = [];
  let i = 0;
  const len = raw.length;

  while (i < len) {
    // Skip whitespace
    if (" \t\r\n".includes(raw[i])) { i++; continue; }

    // Comment
    if (raw[i] === "%") {
      while (i < len && raw[i] !== "\n" && raw[i] !== "\r") i++;
      continue;
    }

    // String literal (...)
    if (raw[i] === "(") {
      let depth = 1;
      let s = "";
      i++; // skip opening (
      while (i < len && depth > 0) {
        if (raw[i] === "\\") {
          i++;
          if (i < len) {
            // Handle common escapes
            switch (raw[i]) {
              case "n": s += "\n"; break;
              case "r": s += "\r"; break;
              case "t": s += "\t"; break;
              case "(": s += "("; break;
              case ")": s += ")"; break;
              case "\\": s += "\\"; break;
              default:
                // Octal escape or just the char
                if (raw[i] >= "0" && raw[i] <= "7") {
                  let oct = raw[i];
                  if (i + 1 < len && raw[i + 1] >= "0" && raw[i + 1] <= "7") { oct += raw[++i]; }
                  if (i + 1 < len && raw[i + 1] >= "0" && raw[i + 1] <= "7") { oct += raw[++i]; }
                  s += String.fromCharCode(parseInt(oct, 8));
                } else {
                  s += raw[i];
                }
            }
          }
        } else if (raw[i] === "(") {
          depth++;
          s += "(";
        } else if (raw[i] === ")") {
          depth--;
          if (depth > 0) s += ")";
        } else {
          s += raw[i];
        }
        i++;
      }
      tokens.push("(" + s + ")");
      continue;
    }

    // Hex string <...>
    if (raw[i] === "<" && (i + 1 >= len || raw[i + 1] !== "<")) {
      let hex = "";
      i++; // skip <
      while (i < len && raw[i] !== ">") {
        if (!" \t\r\n".includes(raw[i])) hex += raw[i];
        i++;
      }
      if (i < len) i++; // skip >
      tokens.push("<" + hex + ">");
      continue;
    }

    // Dict << ... >> - skip as single token
    if (raw[i] === "<" && i + 1 < len && raw[i + 1] === "<") {
      let depth = 1;
      i += 2;
      while (i < len - 1 && depth > 0) {
        if (raw[i] === "<" && raw[i + 1] === "<") { depth++; i += 2; }
        else if (raw[i] === ">" && raw[i + 1] === ">") { depth--; i += 2; }
        else i++;
      }
      tokens.push("<<dict>>");
      continue;
    }

    // Name /xxx
    if (raw[i] === "/") {
      let name = "/";
      i++;
      while (i < len && !" \t\r\n()[]<>/%".includes(raw[i])) {
        name += raw[i];
        i++;
      }
      tokens.push(name);
      continue;
    }

    // Array [...] - just push brackets
    if (raw[i] === "[") { tokens.push("["); i++; continue; }
    if (raw[i] === "]") { tokens.push("]"); i++; continue; }

    // Number or operator (word)
    let word = "";
    while (i < len && !" \t\r\n()[]<>/%".includes(raw[i])) {
      word += raw[i];
      i++;
    }
    if (word) tokens.push(word);
  }

  return tokens;
}

/**
 * Extrae texto de un token string: "(texto)" -> "texto", "<hex>" -> decoded
 */
function extractStringFromToken(token) {
  if (!token) return "";
  if (typeof token !== "string") return String(token);

  // Parenthesized string
  if (token.startsWith("(") && token.endsWith(")")) {
    return token.slice(1, -1);
  }

  // Hex string
  if (token.startsWith("<") && token.endsWith(">")) {
    const hex = token.slice(1, -1);
    let result = "";
    for (let i = 0; i < hex.length - 1; i += 2) {
      const code = parseInt(hex.substring(i, i + 2), 16);
      if (!isNaN(code)) result += String.fromCharCode(code);
    }
    return result;
  }

  return token;
}

/**
 * Reconstruye el texto de un operador TJ (array de strings y n├║meros).
 * Los n├║meros son ajustes de kerning que ignoramos para el texto.
 */
function reconstructTJArray(stack) {
  let text = "";
  let inArray = false;

  for (const t of stack) {
    if (t === "[") { inArray = true; continue; }
    if (t === "]") { inArray = false; continue; }

    if (inArray || stack.length === 1) {
      if (typeof t === "string") {
        if (t.startsWith("(") || t.startsWith("<")) {
          text += extractStringFromToken(t);
        }
        // Numbers (kerning) are ignored for text content
      }
    }
  }

  return text;
}

/**
 * Estimaci├│n simple del ancho de texto (para avanzar tx).
 * No es precisa pero suficiente para nuestros prop├│sitos.
 */
function estimateTextWidth(text, fontSize) {
  // Aproximaci├│n: cada car├ícter ocupa ~0.5 * fontSize en promedio
  return text.length * fontSize * 0.5;
}

/**
 * Obtiene los bytes del content stream de una p├ígina PDF.
 * Maneja tanto streams simples como arrays de streams.
 */
function getPageContentBytes(page, pdfDoc) {
  try {
    const node = page.node;
    const contentsRef = node.get(PDFName.of("Contents"));

    if (!contentsRef) return null;

    // Resolver la referencia
    const contents = pdfDoc.context.lookup(contentsRef);

    if (!contents) return null;

    // Si es un array de streams, concatenarlos
    if (contents instanceof PDFArray) {
      const allBytes = [];
      for (let i = 0; i < contents.size(); i++) {
        const streamRef = contents.get(i);
        const stream = pdfDoc.context.lookup(streamRef);
        if (stream && stream instanceof PDFRawStream) {
          try {
            const decoded = decodePDFRawStream(stream).decode();
            allBytes.push(decoded);
          } catch (e) {
            console.warn("ÔÜá´©Å Error decodificando stream parcial:", e.message);
          }
        }
      }
      if (allBytes.length === 0) return null;
      // Concatenar todos los arrays de bytes
      const totalLen = allBytes.reduce((sum, b) => sum + b.length, 0);
      const result = new Uint8Array(totalLen);
      let offset = 0;
      for (const b of allBytes) {
        result.set(b, offset);
        offset += b.length;
      }
      return result;
    }

    // Stream simple
    if (contents instanceof PDFRawStream) {
      return decodePDFRawStream(contents).decode();
    }

    // Si es otro tipo de stream con m├®todos de contenido
    if (contents && typeof contents === "object") {
      try {
        const encoded = contents.getUnencodedContents?.() || contents.getContents?.();
        if (encoded) return encoded;
      } catch { /* ignore */ }
    }

    return null;
  } catch (e) {
    console.warn("ÔÜá´©Å Error obteniendo content stream:", e.message);
    return null;
  }
}

/**
 * Detecta autom├íticamente las posiciones de las etiquetas en el PDF.
 * Usa pdf-lib para parsear el content stream directamente (sin web workers).
 * 
 * @param {ArrayBuffer} pdfBytes - Los bytes del PDF
 * @returns {Promise<Object|null>} - Objeto con posiciones detectadas, o null si falla
 */
export async function detectLabelPositions(pdfBytes) {
  try {
    const pdfDoc = await PDFDocument.load(pdfBytes, { ignoreEncryption: true });
    const pages = pdfDoc.getPages();

    if (pages.length === 0) {
      console.warn("ÔÜá´©Å PDF sin p├íginas");
      return null;
    }

    const page = pages[0];
    const pw = page.getWidth();
    const ph = page.getHeight();

    // Obtener bytes del content stream
    const contentBytes = getPageContentBytes(page, pdfDoc);
    if (!contentBytes) {
      console.warn("ÔÜá´©Å No se pudo obtener el content stream de la p├ígina");
      return null;
    }

    // Extraer items de texto con posiciones
    const items = extractTextItems(contentBytes);

    if (items.length === 0) {
      console.warn("ÔÜá´©Å PDF sin texto extra├¡ble desde content stream");
      return null;
    }

    console.log(`­ƒôä Extra├¡dos ${items.length} fragmentos de texto del PDF`);

    // Normalizar items
    const normItems = items.map(it => ({
      ...it,
      norm: norm(it.str),
    }));

    // Buscar cada target - intentar coincidencias concatenando items cercanos
    const positions = {};
    let found = 0;

    for (const t of TARGETS) {
      let hit = null;

      // Primero buscar coincidencia directa en un solo item
      for (const pattern of t.patterns) {
        const needle = norm(pattern);
        hit = normItems.find(it => it.norm.includes(needle));
        if (hit) break;
      }

      // Si no encontramos, intentar concatenar items en la misma l├¡nea (misma Y ┬▒ 2)
      if (!hit) {
        for (const pattern of t.patterns) {
          const needle = norm(pattern);
          // Agrupar items por Y similar
          const yGroups = {};
          for (const it of normItems) {
            const yKey = Math.round(it.y / 2) * 2; // agrupar por Y redondeado a 2
            if (!yGroups[yKey]) yGroups[yKey] = [];
            yGroups[yKey].push(it);
          }

          for (const [, group] of Object.entries(yGroups)) {
            // Ordenar por X
            group.sort((a, b) => a.x - b.x);
            const concat = group.map(g => g.norm).join(" ");
            if (concat.includes(needle)) {
              // Encontrado en la l├¡nea concatenada - usar el primer item
              hit = group[0];
              // Buscar el ├║ltimo fragmento que contiene parte del needle (ej: el ":")
              // para calcular el ancho solo hasta el final de la etiqueta, no toda la l├¡nea
              let labelEndX = hit.x;
              let accumulated = "";
              for (const g of group) {
                accumulated += (accumulated ? " " : "") + g.norm;
                const gEndX = g.x + estimateTextWidth(g.str, g.fontSize);
                if (gEndX > labelEndX) labelEndX = gEndX;
                // Si ya acumulamos suficiente para contener el needle, parar aqu├¡
                if (accumulated.includes(needle)) break;
              }
              hit = {
                ...hit,
                width: labelEndX - hit.x,
              };
              break;
            }
          }
          if (hit) break;
        }
      }

      if (hit) {
        const width = hit.width || estimateTextWidth(hit.str, hit.fontSize);
        positions[t.key] = {
          x: hit.x,
          y: hit.y,
          width: width,
          height: hit.fontSize || 12,
        };
        found++;
        console.log(`Ô£à "${t.key}": x=${hit.x.toFixed(1)}, y=${hit.y.toFixed(1)}, w=${width.toFixed(1)}`);
      } else {
        console.warn(`ÔÜá´©Å No encontrado: "${t.patterns[0]}"`);
      }
    }

    // Si encontramos menos de 2 etiquetas cr├¡ticas, no es fiable
    const critical = ["km", "operarios", "nombre", "firmado"];
    const criticalFound = critical.filter(k => positions[k]).length;
    if (criticalFound < 2) {
      console.warn(`ÔÜá´©Å Solo ${criticalFound} etiquetas cr├¡ticas encontradas, no fiable`);
      return null;
    }

    console.log(`­ƒôÉ Detecci├│n OK: ${found}/${TARGETS.length} etiquetas, p├ígina ${pw.toFixed(0)}x${ph.toFixed(0)}`);

    // === FASE 2: Detectar rect├íngulos para encontrar los recuadros reales ===
    let boxes = {};
    try {
      const rects = extractRectangles(contentBytes);
      console.log(`­ƒôª Extra├¡dos ${rects.length} rect├íngulos del PDF`);

      // Buscar el recuadro de "Firmado"
      if (positions.firmado) {
        const firmaBox = findBoxNearLabel(rects, positions.firmado.x, positions.firmado.y);
        if (firmaBox) {
          boxes.firmado = firmaBox;
          console.log(`­ƒôª Box "Firmado": x=${firmaBox.x.toFixed(1)}, y=${firmaBox.y.toFixed(1)}, w=${firmaBox.w.toFixed(1)}, h=${firmaBox.h.toFixed(1)}`);
        } else {
          console.warn("ÔÜá´©Å No se encontr├│ recuadro cerca de 'Firmado'");
        }
      }

      // Buscar el recuadro de "Datos t├®cnico" que contiene Nombre y DNI
      if (positions.nombre) {
        const datosBox = findBoxContainingLabel(rects, positions.nombre.x, positions.nombre.y);
        if (datosBox) {
          boxes.datosTecnico = datosBox;
          console.log(`­ƒôª Box "Datos t├®cnico": x=${datosBox.x.toFixed(1)}, y=${datosBox.y.toFixed(1)}, w=${datosBox.w.toFixed(1)}, h=${datosBox.h.toFixed(1)}`);
        }
      }

      // Log all rects for debugging (only significant ones)
      const sigRects = rects.filter(r => r.w > 50 && r.h > 20);
      if (sigRects.length > 0) {
        console.log(`­ƒôª Rect├íngulos significativos (${sigRects.length}):`);
        sigRects.forEach((r, i) => {
          console.log(`  [${i}] x=${r.x.toFixed(1)}, y=${r.y.toFixed(1)}, w=${r.w.toFixed(1)}, h=${r.h.toFixed(1)}`);
        });
      }
    } catch (rectErr) {
      console.warn("ÔÜá´©Å Error extrayendo rect├íngulos:", rectErr.message);
    }

    return {
      viewport: { width: pw, height: ph },
      positions,
      boxes,
    };
  } catch (error) {
    console.error("ÔØî Error detectando posiciones:", error.message);
    return null;
  }
}
